import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import {
  FAST_APPOINTMENTS_COLOR,
  FAST_APPOINTMENTS_LABEL,
  FAST_APPOINTMENTS_STATUS,
  FastAppointmentValues
} from 'constants/appointments';
import { fetchFastAppointments } from 'services/api/fastAppointments';
import { FastAppointmentListRow } from 'types/api/fastAppointments';

import useOrgEffect from 'hooks/useOrgEffect';

const PAGE_LIMIT = 5;
const { Text } = Typography;

const FastAppointmentRequest: FC = () => {
  const [results, setResults] = useState<FastAppointmentListRow[]>([]);
  const [loading, setLoading] = useState(true);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchFastAppointments({
          page: 1,
          limit: PAGE_LIMIT
        });

        if (!cancelled) setResults(res.results);
      } catch {
        // swallow — empty widget on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Table
      dataSource={results}
      columns={[
        {
          title: 'Patient Name',
          dataIndex: 'first_name',
          key: 'name',
          render(_, record) {
            return `${record.first_name} ${record.last_name}`;
          }
        },
        {
          title: 'Contact',
          dataIndex: 'email',
          key: 'email',
          render(_, record) {
            return (
              <>
                <Text>
                  Phone: <a href={`tel:${record.phone}`}>{record.phone}</a>
                  <span>, </span>
                  Email: <a href={`mailto:${record.email}`}>{record.email}</a>
                </Text>
              </>
            );
          }
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          render(value: FastAppointmentValues) {
            return (
              <Tag color={FAST_APPOINTMENTS_COLOR[value]}>
                {FAST_APPOINTMENTS_LABEL[value]}
              </Tag>
            );
          }
        },
        {
          title: 'Created at',
          dataIndex: 'created_at',
          key: 'created_at',
          render(createdAt: string) {
            return dayjs(createdAt).format(FULL_DATE_FORMAT);
          }
        },
        {
          title: '',
          dataIndex: 'id',
          key: 'id',
          align: 'right',
          render: id => (
            <Link
              style={{ cursor: 'pointer', color: '#2552F3' }}
              to={`/appointments/patient-requests/${id}`}
            >
              <b>View Request</b>
            </Link>
          )
        }
      ]}
      loading={loading}
      rowKey="id"
      className="table_head"
      pagination={false}
      rowClassName={record =>
        record.status === FAST_APPOINTMENTS_STATUS.new ? 'table-row-new' : ''
      }
    />
  );
};

export default FastAppointmentRequest;
